import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MessageCircle, ArrowRight, Diamond, Award, Sparkles, Shield } from 'lucide-react';

export const YuvelirnyeIzdeliyaRiga = () => {
  return (
    <>
      <Helmet>
        <title>Ювелирные изделия в Риге | Украшения с бриллиантами | Pure Atelier</title>
        <meta name="description" content="Эксклюзивные ювелирные изделия в Риге. Кольца, серьги с натуральными бриллиантами. Высочайшее качество, лучшие цены. Pure Atelier Riga." />
        <meta name="keywords" content="ювелирные изделия Рига, украшения с бриллиантами, ювелир Рига, jewelry Riga" />
        <link rel="canonical" href="https://pureatelier.lv/yuvelirnye-izdeliya-riga" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/1458867/pexels-photo-1458867.jpeg?auto=compress&cs=tinysrgb&w=1920')] bg-cover bg-center opacity-10"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-6">
                Pure Atelier Riga
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-stone-900 mb-6 leading-tight">
                Ювелирные изделия в Риге
              </h1>
              <p className="text-xl text-stone-600 mb-8 max-w-2xl mx-auto">
                Эксклюзивные украшения с натуральными бриллиантами. Кольца и серьги, созданные с мастерством и любовью.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/gallery">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8">
                    Смотреть всю коллекцию <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="https://wa.me/37128448991?text=Здравствуйте!%20Интересуют%20ювелирные%20изделия." target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50 px-8">
                    <MessageCircle className="mr-2 h-5 w-5" /> Связаться с нами
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-serif text-stone-900 mb-12 text-center">Наша коллекция</h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <Link to="/gallery?category=rings" className="group">
                <div className="relative overflow-hidden rounded-2xl bg-stone-100 p-8 hover:bg-stone-200 transition-colors">
                  <Diamond className="h-16 w-16 text-amber-600 mb-4" />
                  <h3 className="text-2xl font-serif text-stone-900 mb-2">Кольца</h3>
                  <p className="text-stone-600 mb-4">Помолвочные кольца и кольца с бриллиантами</p>
                  <span className="text-amber-600 font-medium group-hover:underline">Смотреть коллекцию колец →</span>
                </div>
              </Link>
              <Link to="/gallery?category=earrings" className="group">
                <div className="relative overflow-hidden rounded-2xl bg-stone-100 p-8 hover:bg-stone-200 transition-colors">
                  <Sparkles className="h-16 w-16 text-amber-600 mb-4" />
                  <h3 className="text-2xl font-serif text-stone-900 mb-2">Серьги</h3>
                  <p className="text-stone-600 mb-4">Элегантные серьги с натуральными бриллиантами</p>
                  <span className="text-amber-600 font-medium group-hover:underline">Смотреть коллекцию серёг →</span>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 bg-stone-50">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { icon: Diamond, title: "Натуральные бриллианты", desc: "Сертификаты GIA/IGI" },
                { icon: Award, title: "Сделано в Латвии", desc: "Работа местных мастеров" },
                { icon: Sparkles, title: "Уникальный дизайн", desc: "Индивидуальный подход" },
                { icon: Shield, title: "Гарантия", desc: "Полная гарантия на изделия" }
              ].map((item, i) => (
                <div key={i} className="text-center p-6">
                  <item.icon className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-stone-900 mb-2">{item.title}</h3>
                  <p className="text-stone-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-serif text-stone-900 mb-8 text-center">
                О Pure Atelier Riga
              </h2>
              
              <div className="prose prose-lg max-w-none text-stone-700">
                <p>
                  <strong>Pure Atelier</strong> — это ювелирная мастерская в Риге, специализирующаяся на эксклюзивных 
                  украшениях с натуральными бриллиантами. Мы предлагаем как готовые изделия, так и индивидуальный дизайн на заказ.
                </p>
                
                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Наши ценности</h3>
                <ul>
                  <li><strong>Качество</strong> — только материалы и бриллианты высочайшего качества</li>
                  <li><strong>Прозрачность</strong> — честные цены без скрытых наценок</li>
                  <li><strong>Индивидуальность</strong> — каждый клиент для нас особенный</li>
                  <li><strong>Мастерство</strong> — опытные латвийские ювелиры</li>
                </ul>

                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Почему Pure Atelier?</h3>
                <p>
                  Мы — местная латвийская компания, работающая напрямую без посредников. Это позволяет нам предлагать 
                  лучшие цены в Риге без потери качества. Каждое наше изделие — это произведение искусства, созданное с заботой.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-stone-900 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-serif mb-4">Посетите нашу коллекцию</h2>
            <p className="text-stone-300 mb-8 max-w-2xl mx-auto">
              Свяжитесь с нами через WhatsApp для бесплатной консультации по любому ювелирному изделию.
            </p>
            <a href="https://wa.me/37128448991?text=Здравствуйте!%20Интересуют%20ювелирные%20изделия." target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8">
                <MessageCircle className="mr-2 h-5 w-5" /> Консультация в WhatsApp
              </Button>
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default YuvelirnyeIzdeliyaRiga;
